import { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import Icon from "@mui/material/Icon";
import { enqueueSnackbar } from "notistack";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useICCCColors } from "../components/theme";
import { useUpdateCoordinateMutation } from "features/icccApiSlice";
import DeviceDetailDialog from "../components/dialogs/DeviceDetailDialog";
import MapTopBar from "./MapTopBar";
import MapCanvas from "./MapCanvas";
import MapToolbar from "./MapToolbar";
import MapFilters from "./MapFilters";
import DevicePanel from "./DevicePanel";
import useMapData from "./hooks/useMapData";

const DEFAULT_VIEW = { scale: 1, translate: { x: 0, y: 0 } };
const MIN_SCALE = 0.5;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.2;
// Coordinates are unvalidated raw strings server-side — debounce so a drag
// gesture doesn't fire an update per pointermove tick.
const COORDINATE_SAVE_DEBOUNCE_MS = 400;

const fmtTimestamp = (ms) =>
  ms
    ? new Date(ms).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
    : "Loading…";

export default function MapView() {
  const ICCC_COLORS = useICCCColors();
  const { devices, locations, isLoading, isFetching, refetch, lastUpdated } = useMapData();
  const [updateCoordinate] = useUpdateCoordinateMutation();

  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [groupFilter, setGroupFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [nameFilter, setNameFilter] = useState("");
  const [ipFilter, setIpFilter] = useState("");
  const [editable, setEditable] = useState(false);
  const [view, setView] = useState(DEFAULT_VIEW);
  const [detailDevice, setDetailDevice] = useState(null);

  useEffect(() => {
    if (!selectedLocationId && locations.length) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  const selectedLocation = useMemo(
    () => locations.find((l) => l.id === selectedLocationId) || null,
    [locations, selectedLocationId]
  );

  const locationDevices = useMemo(
    () => (selectedLocation ? devices.filter((d) => d.location === selectedLocation.code) : []),
    [devices, selectedLocation]
  );

  const filteredDevices = useMemo(
    () =>
      locationDevices.filter(
        (d) =>
          (!groupFilter || d.category === groupFilter) &&
          (!statusFilter || d.health === statusFilter) &&
          (!nameFilter || (d.nodeName || "").toLowerCase().includes(nameFilter.trim().toLowerCase())) &&
          (!ipFilter || (d.ipAddress || "").toLowerCase().includes(ipFilter.trim().toLowerCase()))
      ),
    [locationDevices, groupFilter, statusFilter, nameFilter, ipFilter]
  );

  const unplacedDevices = useMemo(
    () => filteredDevices.filter((d) => d.xCoordinate == null || d.yCoordinate == null),
    [filteredDevices]
  );

  const placedDevices = useMemo(
    () => filteredDevices.filter((d) => d.xCoordinate != null && d.yCoordinate != null),
    [filteredDevices]
  );

  // Reset transient view/filter state whenever the active location changes.
  useEffect(() => {
    setView(DEFAULT_VIEW);
    setGroupFilter(null);
    setStatusFilter(null);
    setNameFilter("");
    setIpFilter("");
  }, [selectedLocationId]);

  // Keyed by device id: the in-flight debounce timer plus the coordinate it
  // will save, so a component unmount can flush it immediately instead of
  // silently dropping a just-completed drag (see persistCoordinate below).
  const pendingSaves = useRef(new Map());
  useEffect(
    () => () => {
      pendingSaves.current.forEach(({ timer, id, xCoordinate, yCoordinate }) => {
        clearTimeout(timer);
        updateCoordinate({ id, xCoordinate, yCoordinate }).catch(() => {});
      });
    },
    [updateCoordinate]
  );

  const persistCoordinate = (id, xCoordinate, yCoordinate) => {
    const existing = pendingSaves.current.get(id);
    if (existing) clearTimeout(existing.timer);
    const timer = setTimeout(() => {
      updateCoordinate({ id, xCoordinate, yCoordinate })
        .unwrap()
        .catch(() =>
          enqueueSnackbar("Failed to save device position — please try again", {
            variant: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          })
        );
      pendingSaves.current.delete(id);
    }, COORDINATE_SAVE_DEBOUNCE_MS);
    pendingSaves.current.set(id, { timer, id, xCoordinate, yCoordinate });
  };

  const handleMarkerDragEnd = (device, xPct, yPct) => {
    persistCoordinate(device.id, xPct.toFixed(2), yPct.toFixed(2));
  };

  const handleUnplacedDrop = (assetId, xPct, yPct) => {
    persistCoordinate(Number(assetId), xPct.toFixed(2), yPct.toFixed(2));
  };

  return (
    <MDBox
      sx={{
        px: 1,
        py: 1,
        bgcolor: ICCC_COLORS.pageBg,
        color: ICCC_COLORS.textPrimary,
        borderRadius: 1.5,
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <MDBox
        sx={{
          bgcolor: ICCC_COLORS.cardBg,
          border: `1px solid ${ICCC_COLORS.cardBorder}`,
          borderRadius: 1.5,
          px: 2.5,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: ICCC_COLORS.shadow,
        }}
      >
        <MDBox sx={{ flex: 1, minWidth: 0 }}>
          <MDTypography
            variant="h3"
            fontWeight="bold"
            sx={{ color: ICCC_COLORS.textPrimary, lineHeight: 1, fontSize: 30 }}
          >
            ICCC Map View
          </MDTypography>
          <MDTypography variant="body2" sx={{ color: ICCC_COLORS.textSecondary, mt: 0.25 }}>
            Site / floor map device monitoring
          </MDTypography>
        </MDBox>

        <MDTypography variant="caption" sx={{ color: ICCC_COLORS.textSecondary }}>
          Last Updated: {fmtTimestamp(lastUpdated)}
        </MDTypography>

        <Tooltip title="Refresh now">
          <IconButton size="small" onClick={refetch} sx={{ color: ICCC_COLORS.accent }}>
            {isFetching ? (
              <CircularProgress size={16} sx={{ color: ICCC_COLORS.accent }} />
            ) : (
              <Icon fontSize="small">refresh</Icon>
            )}
          </IconButton>
        </Tooltip>
      </MDBox>

      {isLoading ? (
        <MDBox sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: ICCC_COLORS.accent }} />
        </MDBox>
      ) : !locations.length ? (
        <MDBox
          sx={{
            bgcolor: ICCC_COLORS.cardBg,
            border: `1px solid ${ICCC_COLORS.cardBorder}`,
            borderRadius: 1.5,
            p: 4,
            textAlign: "center",
            color: ICCC_COLORS.textMuted,
          }}
        >
          No locations have a Background Image URL set yet. Add one in Location Master to see it here.
        </MDBox>
      ) : (
        <>
          <MapTopBar
            locations={locations}
            selectedLocationId={selectedLocationId}
            onSelectLocation={setSelectedLocationId}
          />

          <MapFilters
            devices={locationDevices}
            groupFilter={groupFilter}
            onGroupFilterChange={setGroupFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            nameFilter={nameFilter}
            onNameFilterChange={setNameFilter}
            ipFilter={ipFilter}
            onIpFilterChange={setIpFilter}
          />

          {/* Toolbar lives above the map — outside the image container — so the
              Edit-layout controls never cover the map itself. */}
          <MDBox sx={{ display: "flex" }}>
            <MapToolbar
              editable={editable}
              onEditableChange={setEditable}
              onZoomIn={() => setView((v) => ({ ...v, scale: Math.min(MAX_SCALE, v.scale + ZOOM_STEP) }))}
              onZoomOut={() => setView((v) => ({ ...v, scale: Math.max(MIN_SCALE, v.scale - ZOOM_STEP) }))}
              onReset={() => setView(DEFAULT_VIEW)}
            />
          </MDBox>

          <MDBox sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            {editable && (
              <DevicePanel
                placedDevices={placedDevices}
                unplacedDevices={unplacedDevices}
                onPlacedDeviceClick={setDetailDevice}
              />
            )}
            {/* Square canvas (height = width) — the page scrolls if it exceeds
                the viewport; the map image fills it edge-to-edge. */}
            <MDBox sx={{ position: "relative", flex: 1, minWidth: 0, aspectRatio: "1 / 1" }}>
              <MapCanvas
                backgroundUrl={selectedLocation?.backgroundImageURL}
                devices={filteredDevices}
                editable={editable}
                scale={view.scale}
                translate={view.translate}
                onViewChange={setView}
                onMarkerClick={setDetailDevice}
                onMarkerDragEnd={handleMarkerDragEnd}
                onUnplacedDrop={handleUnplacedDrop}
              />
            </MDBox>
          </MDBox>
        </>
      )}

      <DeviceDetailDialog
        open={!!detailDevice}
        device={detailDevice}
        editable={editable}
        onClose={() => setDetailDevice(null)}
      />
    </MDBox>
  );
}
